import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "reactstrap";
import { useRecoilState, useRecoilValue } from "recoil";
import { authState } from "src/recoil/atoms/auth";
import { LogOut, Server, Settings } from "react-feather";
import ProfileDropdown from "./ProfileDropdown";
import { ACCESS_TOKEN, removeLocalStorage } from "src/utils/storage";
import { sidebarState } from "src/recoil/atoms/sidebar";
import { REACT_APP_PORTAL_URL } from "src/constant/environment";
import FormSelect from "src/components/Common/Form/FormSelect";
import { useForm } from "react-hook-form";
import useUrlState from "@ahooksjs/use-url-state";
import { projectListState } from "src/recoil/atoms/global";
import ModalServerStatusCheck from "../Modal/ModalServerStatusCheck";
import ModalSettings from "../Modal/ModalSettings";
import useScrollbarDetection from "src/Hooks/useScrollbarDetection";

const FORM_NAME = {
  CURRENT_PROJECT: "currentProject",
};

const TopBar = () => {
  const [auth, setAuth] = useRecoilState(authState);
  const [sidebar, setSidebar] = useRecoilState(sidebarState);
  const { t } = useTranslation("topbar");
  const [projectOptions, setProjectOptions] = useState([]);
  const [isServerStatusModalOpen, setIsServerStatusModalOpen] = useState(false);
  const [isSettingModalOpen, setIsSettingModalOpen] = useState(false);
  const projectList = useRecoilValue(projectListState);

  const hasScrollbar = useScrollbarDetection(".ps-sidebar-root .ps-sidebar-container", [sidebar.collapsed]);

  const [params] = useUrlState(
    {},
    {
      stringifyOptions: {
        skipNull: true,
        skipEmptyString: true,
      },
    }
  );

  const { control, setValue } = useForm({
    defaultValues: {},
  });

  useEffect(() => {
    if (projectList) {
      const projectListOptions = projectList.map((project) => ({
        label: project.name,
        value: project.id,
      }));
      setProjectOptions(projectListOptions);
      if (params?.projectId) {
        setValue(FORM_NAME.CURRENT_PROJECT, params.projectId);
      }
    }
  }, [projectList]);

  const handleLogout = (e) => {
    e.preventDefault();
    removeLocalStorage(ACCESS_TOKEN);
    setAuth({});
    window.location.href = `${REACT_APP_PORTAL_URL}/logout`;
  };

  const logoutMenu = {
    label: "Logout",
    icon: LogOut,
    onClick: handleLogout
  };

  const [profileMenu, setProfileMenu] = useState([]);

  const handleChangeProject = (selectOption) => {
    const projectId = selectOption?.value || selectOption;
    const url = new URL(window.location.origin);
    url.searchParams.set('projectId', projectId);
    window.location.href = url.toString();
  }

  const handleSidebarClick = () => {
    if (sidebar.broken) {
      setSidebar({...sidebar, toggled: !sidebar.toggled});
      return;
    }
    setSidebar({...sidebar, collapsed: !sidebar.collapsed});
  }

  useEffect(() => {
    const profileMenus = [logoutMenu];
    if (auth?.has_permission_reboot) {
      profileMenus.unshift({
        label: "Server Status Check",
        icon: Server,
        onClick: () => setIsServerStatusModalOpen(true),
      });
    }
    if (auth?.has_permission_setting_automation_workflow) {
      profileMenus.unshift({
        label: "Settings",
        icon: Settings,
        onClick: () => setIsSettingModalOpen(true),
      });
    }
    setProfileMenu(profileMenus);
    
  }, [auth]);

  const getTopBarPadding = () => {
    if (sidebar.broken) return "p-0";

    const isCollapsedWithScrollbar = sidebar.collapsed && hasScrollbar;
    const isCollapsedWithoutScrollbar = sidebar.collapsed && !hasScrollbar;
    const isExpandedWithScrollbar = !sidebar.collapsed && hasScrollbar;

    if (isCollapsedWithScrollbar) return "p-l-100";
    if (isCollapsedWithoutScrollbar) return "p-l-80";
    if (isExpandedWithScrollbar) return "p-l-260";

    return "p-l-250";
  };

  return (
    <div className={`navbar navbar-expand flex-column flex-md-row navbar-custom bg-white ${getTopBarPadding()}`}>
      <Container fluid className="bg-white z-i-1005">
        <div className="d-flex align-items-center gap-2 height-by-px-70 justify-content-between w-100">
          <div className="d-flex gap-2 align-items-center ">
            <button className="btn btn-blank p-20 border-0" onClick={() => handleSidebarClick()}>
              <i className="bi bi-list f-s-30" style={{ color: 'gray' }}></i>
            </button>
            <span className="project-title-header">Project</span>
            <FormSelect
              name={FORM_NAME.CURRENT_PROJECT}
              classGroup="width-by-px-400 mb-0"
              control={control}
              placeholder={t("switchProject")}
              isSearchable
              onChangeExtra={handleChangeProject}
              options={projectOptions}
            />
          </div>

          <div className="d-flex align-items-center gap-2 mx-2">
            <ProfileDropdown
              menuItems={profileMenu}
              username={auth?.name}
              avatar={auth?.image_url}
            />
          </div>
        </div>
        <ModalServerStatusCheck
          toggle={isServerStatusModalOpen}
          closeModal={() => setIsServerStatusModalOpen(false)}
        />
        <ModalSettings
          toggle={isSettingModalOpen}
          closeModal={() => setIsSettingModalOpen(false)}
        />
      </Container>
    </div>
  );
};

export default TopBar;
